import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { subscriptionService } from '../../services/subscriptionService';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const PricingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    const { plans: plansData } = await subscriptionService?.getPlans();
    setPlans(plansData);
    setLoading(false);
  };

  const handleSelectPlan = async (plan) => {
    if (!user) {
      navigate('/signup', { state: { selectedPlan: plan?.id } });
      return;
    }

    setProcessingPlan(plan?.id);
    const { url, error } = await subscriptionService?.createCheckoutSession(
      plan?.id,
      user?.id,
      user?.email
    );

    if (error) {
      alert('Erro ao criar sessão de pagamento. Tente novamente.');
      setProcessingPlan(null);
      return;
    }

    if (url) {
      window.location.href = url;
    }
  };

  const renderFeature = (feature) => {
    const icon = feature?.included ? 'Check' : 'X';
    const color = feature?.included ? 'text-green-600' : 'text-red-500';
    
    return (
      <div key={feature?.text} className="flex items-start gap-3">
        <Icon name={icon} size={20} className={`${color} mt-0.5 flex-shrink-0`} />
        <span className={feature?.included ? 'text-gray-700' : 'text-gray-400 line-through'}>
          {feature?.text}
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Carregando planos...</p>
        </div>
      </div>
    );
  }

  const starterPlan = plans?.find(p => p?.name === 'starter');
  const profissionalPlan = plans?.find(p => p?.name === 'profissional');
  const scalePlan = plans?.find(p => p?.name === 'scale');

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="/assets/images/Captura_de_tela_2026-02-06_014642-1770355644537.png" 
              alt="VERTEX COMMERCE Logo" 
              className="w-10 h-10 object-contain"
            />
            <span className="font-bold text-xl text-gray-900">VERTEX COMMERCE</span>
          </div>
          <div className="flex items-center gap-4">
            {user ? (
              <Button onClick={() => navigate('/dashboard-overview')}>
                Dashboard
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')}>
                  Entrar
                </Button>
                <Button onClick={() => navigate('/signup')}>
                  Criar Conta
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Pricing Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Escolha seu plano
          </h1>
          <p className="text-xl text-gray-600">
            Comece a vender com estratégia hoje mesmo
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {/* Starter Plan */}
          {starterPlan && (
            <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8 hover:shadow-xl transition-shadow">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{starterPlan?.display_name}</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">R${starterPlan?.price_brl}</span>
                <span className="text-gray-600">/mês</span>
              </div>
              <div className="space-y-4 mb-8">
                {starterPlan?.features?.map(renderFeature)}
              </div>
              <Button 
                fullWidth 
                variant="outline"
                onClick={() => handleSelectPlan(starterPlan)}
                loading={processingPlan === starterPlan?.id}
                disabled={processingPlan !== null}
              >
                Começar Starter
              </Button>
            </div>
          )}

          {/* Profissional Plan (Highlighted) */}
          {profissionalPlan && (
            <div className="bg-white rounded-2xl shadow-2xl border-4 border-blue-600 p-8 transform scale-105 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                  MAIS ESCOLHIDO
                </span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2 mt-4">{profissionalPlan?.display_name}</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-blue-600">R${profissionalPlan?.price_brl}</span>
                <span className="text-gray-600">/mês</span>
              </div>
              <div className="space-y-4 mb-8">
                {profissionalPlan?.features?.map(renderFeature)}
              </div>
              <Button 
                fullWidth
                onClick={() => handleSelectPlan(profissionalPlan)}
                loading={processingPlan === profissionalPlan?.id}
                disabled={processingPlan !== null}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Icon name="Zap" size={20} />
                Começar agora por R$97
              </Button>
            </div>
          )}

          {/* Scale Plan */}
          {scalePlan && (
            <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8 hover:shadow-xl transition-shadow">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{scalePlan?.display_name}</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">R${scalePlan?.price_brl}</span>
                <span className="text-gray-600">/mês</span>
              </div>
              <div className="space-y-4 mb-6">
                {scalePlan?.features?.map(renderFeature)}
              </div>
              <p className="text-sm text-gray-600 mb-6 italic">
                Indicado para operações maiores.
              </p>
              <Button 
                fullWidth 
                variant="outline"
                onClick={() => handleSelectPlan(scalePlan)}
                loading={processingPlan === scalePlan?.id}
                disabled={processingPlan !== null}
              >
                Falar com comercial
              </Button>
            </div>
          )}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16 max-w-3xl mx-auto">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8 rounded-2xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Você pode continuar vendendo no improviso.
            </h2>
            <p className="text-xl mb-4">
              Ou pode operar com estratégia.
            </p>
            <p className="text-lg mb-6">
              Se você já vende, o plano Profissional é o único que realmente faz sentido.
            </p>
            <Button 
              size="xl" 
              variant="secondary" 
              onClick={() => profissionalPlan && handleSelectPlan(profissionalPlan)}
              loading={processingPlan === profissionalPlan?.id}
              disabled={processingPlan !== null}
              className="bg-white text-blue-600 hover:bg-gray-100"
            >
              Começar agora por R$97/mês
            </Button>
            <p className="text-sm mt-4 text-blue-100">
              Cancele quando quiser.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;